import { useAuth } from '@clerk/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createTabLeader, createTabId } from '@/lib/tab-leader';
import { resolveWsUrl } from '@/plugin/ws-endpoint';
import { isOrganizationSuspended } from '@/store/base-query';
import type { WebSocketMessage } from '@/hooks/use-websocket.hook';

export type MessageCategory =
  | 'smartColumns'
  | 'enrichment'
  | 'leads'
  | 'actionColumns'
  | 'credits'
  | 'onboarding'
  | 'presence'
  | 'chat';

type MessageHandler = (message: WebSocketMessage) => void;

// Outbound messages the client may send to the server. Keep this in sync
// with the server-side handler in cleanse_api/api/unified_ws_api.py.
export type WsClientMessage =
  | { type: 'subscribe'; list_id: string }
  | { type: 'unsubscribe'; list_id: string }
  | { type: 'presence' }
  | { type: 'ping' };

// Broadcast channel message types
type WsBroadcastMessage =
  | { type: 'ws_message'; message: WebSocketMessage }
  | {
      type: 'subscription_update';
      listId: string;
      action: 'subscribe' | 'unsubscribe';
      tabId: string;
    }
  | { type: 'subscriptions_request' }
  | { type: 'ws_connection_status'; connected: boolean }
  | { type: 'ws_status_request' };

interface WebSocketContextValue {
  isConnected: boolean;
  isLeader: boolean;
  subscribe: (category: MessageCategory, handler: MessageHandler) => () => void;
  subscribeToList: (listId: string) => void;
  unsubscribeFromList: (listId: string) => void;
  sendMessage: (message: WsClientMessage) => boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

const DEFAULT_CONFIG = {
  maxReconnectAttempts: 10,
  reconnectDelay: 3000,
  pingInterval: 30000,
};

// Server-emitted ack/keepalive types that have no client-side handler.
const KNOWN_NOOP_MESSAGE_TYPES = new Set<string>([
  'subscribed',
  'unsubscribed',
  'pong',
  'error',
]);

function getMessageCategory(message: WebSocketMessage): MessageCategory | null {
  switch (message.type) {
    case 'smart_column_update':
    case 'smart_column_lead_update':
    case 'smart_column_company_update':
    case 'smart_column_company_lead_update':
    case 'crm_lookup_update':
    case 'crm_lookup_lead_update':
      return 'smartColumns';
    // enrichment_results_ready: sent the moment a found contact is saved,
    // before the workflow's billing / CRM tail. Consumers that want only
    // terminal frames filter by type (useEnrichmentWebSocket keeps just
    // enrichment_update).
    case 'enrichment_update':
    case 'enrichment_results_ready':
    case 'bulk_prospecting_update':
      return 'enrichment';
    case 'lead_created':
    case 'lead_updated':
    case 'lead_deleted':
    case 'lead_list_update':
    case 'bulk_operation_complete':
    case 'find_people_progress':
    case 'lead_list_companies_updated':
    case 'personal_email_progress':
    case 'company_enrichment_progress':
      return 'leads';
    case 'action_column_lead_update':
    case 'action_column_progress':
      return 'actionColumns';
    case 'credit_update':
      return 'credits';
    case 'onboarding_step_completed':
      return 'onboarding';
    case 'copilot_message_added':
      return 'chat';
    case 'presence':
      return 'presence';
    default:
      return null;
  }
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { getToken } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const tabIdRef = useRef<string>(createTabId());
  const [isConnected, setIsConnected] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  // Follower tabs track the leader's connection status
  const [leaderConnected, setLeaderConnected] = useState(false);

  // Store callbacks in refs to avoid reconnection loops
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Refs mirroring state so async/broadcast handlers see the current value
  // without re-subscribing when state changes.
  const isLeaderRef = useRef(isLeader);
  isLeaderRef.current = isLeader;
  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;

  // Message handlers by category
  const handlersRef = useRef<Map<MessageCategory, Set<MessageHandler>>>(
    new Map()
  );

  // List subscriptions tracking (listId -> Set of tabIds)
  const subscriptionsRef = useRef<Map<string, Set<string>>>(new Map());
  const currentListIdRef = useRef<string | null>(null);

  // Route a message to all registered handlers for its category
  const routeMessage = useCallback((message: WebSocketMessage) => {
    const category = getMessageCategory(message);
    if (!category) {
      // Server emits ack-style messages (subscribed/unsubscribed/pong)
      // that don't map to a category - silently ignore them.
      if (import.meta.env.DEV && !KNOWN_NOOP_MESSAGE_TYPES.has(message.type)) {
        console.log('[WS] Unknown message category for:', message.type);
      }
      return;
    }

    const handlers = handlersRef.current.get(category);
    if (import.meta.env.DEV) {
      console.log(
        '[WS] Routing message:',
        message.type,
        'to category:',
        category,
        'handlers:',
        handlers?.size ?? 0
      );
    }
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message);
        } catch (e) {
          console.error('Error in WebSocket message handler:', e);
        }
      });
    }
  }, []);

  const postBroadcast = useCallback((msg: WsBroadcastMessage) => {
    broadcastChannelRef.current?.postMessage(msg);
  }, []);

  // Pending subscriptions to send when WebSocket connects
  const pendingSubscriptionsRef = useRef<Set<string>>(new Set());

  // Send message to WebSocket server. Returns true if the socket was open
  // and the frame was queued, false otherwise (caller decides whether to
  // queue for later).
  const sendMessage = useCallback((message: WsClientMessage): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Handle subscription updates (leader only)
  const handleSubscriptionUpdate = useCallback(
    (payload: {
      listId: string;
      action: 'subscribe' | 'unsubscribe';
      tabId: string;
    }) => {
      if (!payload.listId) return;
      const subscriptions = subscriptionsRef.current;
      const current = subscriptions.get(payload.listId) ?? new Set<string>();
      const beforeSize = current.size;

      if (payload.action === 'subscribe') {
        current.add(payload.tabId);
      } else {
        current.delete(payload.tabId);
      }

      if (current.size > 0) {
        subscriptions.set(payload.listId, current);
      } else {
        subscriptions.delete(payload.listId);
      }

      // Send subscribe/unsubscribe to server when first/last tab subscribes
      if (beforeSize === 0 && current.size > 0) {
        const sent = sendMessage({
          type: 'subscribe',
          list_id: payload.listId,
        });
        if (import.meta.env.DEV) {
          console.log('[WS] Subscribe to list:', payload.listId, 'sent:', sent);
        }
        // If WebSocket isn't connected yet, queue the subscription
        if (!sent) {
          pendingSubscriptionsRef.current.add(payload.listId);
          if (import.meta.env.DEV) {
            console.log('[WS] Queued subscription for list:', payload.listId);
          }
        }
      } else if (beforeSize > 0 && current.size === 0) {
        sendMessage({ type: 'unsubscribe', list_id: payload.listId });
        // Remove from pending if it was queued
        pendingSubscriptionsRef.current.delete(payload.listId);
        if (import.meta.env.DEV) {
          console.log('[WS] Unsubscribe from list:', payload.listId);
        }
      }
    },
    [sendMessage]
  );

  const connect = useCallback(async () => {
    // Check if already connected or connecting
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // After many failures keep trying (capped at 30s by the backoff math)
    // instead of giving up - otherwise the indicator gets stuck on
    // "Reconnecting" forever until the user refreshes.
    if (reconnectAttemptsRef.current >= DEFAULT_CONFIG.maxReconnectAttempts) {
      console.warn(
        '[WS] Many reconnect attempts - continuing with capped backoff'
      );
    }

    try {
      const token = await getTokenRef.current();
      if (!token) {
        // Clerk may still be loading - retry shortly so we don't get
        // stuck in "Reconnecting" until the next leadership change.
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, 1000);
        return;
      }

      // VITE_WS_API_URL may be a full URL (as the README documents) or a bare
      // host; the plugin's resolver accepts both.
      const wsUrl = resolveWsUrl({
        apiBase: import.meta.env.VITE_API_URL || 'http://localhost:8000',
        wsApiUrl: import.meta.env.VITE_WS_API_URL,
        location: window.location,
      });

      if (import.meta.env.DEV) {
        console.log('[WS] Connecting to unified endpoint:', wsUrl);
      }

      // Pass the JWT via the Sec-WebSocket-Protocol header instead of the URL
      // query string. Tokens in URLs leak to reverse proxy logs, browser
      // history, and session replay; the subprotocol header is request-scoped
      // and not captured. The backend echoes 'clerk-jwt' back as the accepted
      // subprotocol so the browser keeps the connection open.
      const ws = new WebSocket(wsUrl, ['clerk-jwt', token]);

      ws.onopen = () => {
        if (import.meta.env.DEV) {
          console.log('[WS] Connected to unified WebSocket');
        }
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // Re-subscribe to all tracked lists on reconnect
        if (import.meta.env.DEV) {
          console.log(
            '[WS] Re-subscribing to tracked lists:',
            Array.from(subscriptionsRef.current.keys())
          );
        }
        subscriptionsRef.current.forEach((_tabs, listId) => {
          sendMessage({ type: 'subscribe', list_id: listId });
        });

        // Send any pending subscriptions that were queued before connection
        if (pendingSubscriptionsRef.current.size > 0) {
          if (import.meta.env.DEV) {
            console.log(
              '[WS] Sending pending subscriptions:',
              Array.from(pendingSubscriptionsRef.current)
            );
          }
          pendingSubscriptionsRef.current.forEach((listId) => {
            // Only send if not already tracked in subscriptionsRef (avoid duplicates)
            if (!subscriptionsRef.current.has(listId)) {
              sendMessage({ type: 'subscribe', list_id: listId });
            }
          });
          pendingSubscriptionsRef.current.clear();
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          if (import.meta.env.DEV) {
            console.log('[WS] Received message:', message.type, message);
          }
          // Each tab now opens its own WS, so we no longer fan messages
          // out across tabs via BroadcastChannel - peers receive the
          // same message directly from the server.
          routeMessage(message);
        } catch {
          // Failed to parse WebSocket message
        }
      };

      ws.onclose = (event) => {
        if (import.meta.env.DEV) {
          console.log(
            '[WS] Closed code=%s reason=%s clean=%s',
            event.code,
            event.reason,
            event.wasClean
          );
        }
        // Only clear refs/state if this close belongs to the *current*
        // socket - otherwise a late onclose from a stale socket can wipe
        // out a fresh reconnect we just opened.
        if (wsRef.current === ws) {
          setIsConnected(false);
          wsRef.current = null;
        } else {
          return;
        }

        // Don't reconnect if it was a manual disconnect, nor for a suspended
        // org: the server refuses every socket until staff lift it.
        if (event.code === 1000 || isOrganizationSuspended()) return;

        // Attempt reconnection with exponential backoff
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(
          DEFAULT_CONFIG.reconnectDelay *
            Math.pow(2, reconnectAttemptsRef.current - 1),
          30000
        );

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };

      ws.onerror = (event) => {
        if (import.meta.env.DEV) {
          console.warn('[WS] Error', event);
        }
        if (wsRef.current === ws) {
          setIsConnected(false);
        }
      };

      wsRef.current = ws;
    } catch {
      console.error('Failed to connect to WebSocket');
    }
  }, [routeMessage, sendMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    const leader = createTabLeader({
      channelName: 'cleanlist-ws-leader',
      tabId: tabIdRef.current,
      onLeaderChange: (becameLeader) => {
        setIsLeader(becameLeader);
        if (becameLeader) {
          // Request subscriptions from other tabs when becoming leader
          postBroadcast({ type: 'subscriptions_request' });
        }
      },
    });
    leader.start();

    return () => {
      leader.stop();
    };
  }, [postBroadcast]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel('cleanlist-ws');
    broadcastChannelRef.current = channel;

    channel.onmessage = (event) => {
      const msg = event.data as WsBroadcastMessage;

      switch (msg.type) {
        case 'ws_message':
          // Route messages from leader to local handlers
          routeMessage(msg.message);
          break;
        case 'subscription_update':
          if (isLeaderRef.current) {
            handleSubscriptionUpdate(msg);
          }
          break;
        case 'subscriptions_request':
          // Respond with our current list subscription if we have one
          if (currentListIdRef.current) {
            postBroadcast({
              type: 'subscription_update',
              listId: currentListIdRef.current,
              action: 'subscribe',
              tabId: tabIdRef.current,
            });
          }
          break;
        case 'ws_connection_status':
          // Follower tabs track the leader's connection status
          if (!isLeaderRef.current) {
            setLeaderConnected(msg.connected);
          }
          break;
        case 'ws_status_request':
          // Leader replies so newly mounted follower tabs don't sit on
          // a stale `false` until the next isConnected change.
          if (isLeaderRef.current) {
            postBroadcast({
              type: 'ws_connection_status',
              connected: isConnectedRef.current,
            });
          }
          break;
      }
    };

    // Ask the existing leader (if any) for its current connection status so
    // a follower tab that mounts mid-session doesn't sit on `false` until
    // the leader's next isConnected change.
    postBroadcast({ type: 'ws_status_request' });

    return () => {
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [routeMessage, handleSubscriptionUpdate, postBroadcast]);

  // Connect/disconnect based on leader status
  useEffect(() => {
    if (isLeader) {
      connect();

      // Setup ping interval
      pingIntervalRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
        }
      }, DEFAULT_CONFIG.pingInterval);
    } else {
      disconnect();
      subscriptionsRef.current = new Map();
    }

    return () => {
      if (isLeader) {
        disconnect();
      }
    };
  }, [isLeader, connect, disconnect]);

  // Leader broadcasts connection status to follower tabs
  useEffect(() => {
    if (isLeader) {
      postBroadcast({ type: 'ws_connection_status', connected: isConnected });
    }
  }, [isLeader, isConnected, postBroadcast]);

  // Recover from network drops, sleep/wake, and tab backgrounding. Without
  // this, hitting maxReconnectAttempts (or being put to sleep mid-backoff)
  // leaves the indicator stuck on "Reconnecting" until the user refreshes.
  useEffect(() => {
    if (!isLeader) return;

    const kick = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      connect();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') kick();
    };

    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', kick);

    return () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', kick);
    };
  }, [isLeader, connect]);

  const subscribe = useCallback(
    (category: MessageCategory, handler: MessageHandler): (() => void) => {
      if (!handlersRef.current.has(category)) {
        handlersRef.current.set(category, new Set());
      }
      handlersRef.current.get(category)!.add(handler);

      return () => {
        handlersRef.current.get(category)?.delete(handler);
      };
    },
    []
  );

  const subscribeToList = useCallback(
    (listId: string) => {
      currentListIdRef.current = listId;
      const payload = {
        type: 'subscription_update' as const,
        listId,
        action: 'subscribe' as const,
        tabId: tabIdRef.current,
      };

      if (isLeader) {
        handleSubscriptionUpdate(payload);
      } else {
        postBroadcast(payload);
      }
    },
    [isLeader, handleSubscriptionUpdate, postBroadcast]
  );

  const unsubscribeFromList = useCallback(
    (listId: string) => {
      if (currentListIdRef.current === listId) {
        currentListIdRef.current = null;
      }
      const payload = {
        type: 'subscription_update' as const,
        listId,
        action: 'unsubscribe' as const,
        tabId: tabIdRef.current,
      };

      if (isLeader) {
        handleSubscriptionUpdate(payload);
      } else {
        postBroadcast(payload);
      }
    },
    [isLeader, handleSubscriptionUpdate, postBroadcast]
  );

  const value: WebSocketContextValue = {
    isConnected: isLeader ? isConnected : leaderConnected,
    isLeader,
    subscribe,
    subscribeToList,
    unsubscribeFromList,
    sendMessage,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * The socket when a provider is mounted, null when not. For a hook that only
 * improves on what a fetch already shows (a chat that also works in a test or
 * a surface with no socket), so it must not throw without one.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalWebSocketContext(): WebSocketContextValue | null {
  return useContext(WebSocketContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider'
    );
  }
  return context;
}
